import { useHistoryVideo } from '@/app/main/subadmin/livestreaming/history/HistoryProvider';
import { Button, makeStyles, Menu, MenuItem, Slider, Tooltip, withStyles } from '@material-ui/core';
import dayjs from 'dayjs';
import { useState, useEffect } from 'react';
import {
	MaximizeIcon,
	MinimizeIcon,
	MuteIcon,
	PauseIcon,
	PlayIcon,
	VolumeIcon,
	SettingsIcon,
	BackSixtyIcon,
	BackThirtyIcon,
} from '@playerIcons';

const TimeSlider = withStyles({
	root: {
		color: 'rgb(77, 160, 238)',
		height: 0,
		paddingBottom: '5px',
		width: '100%',
	},
	thumb: {
		height: 10,
		width: 10,
		backgroundColor: '#fff',
		marginTop: -2.5,
		marginLeft: -5,
		'&:focus, &:hover, &$active': {
			boxShadow: 'inherit',
		},
		'&:after': {
			width: 10,
			height: 10,
		},
	},
	active: {},
	valueLabel: {
		left: 'calc(-50% + 4px)',
	},
	track: {
		height: 5,
		borderRadius: 4,
		backgroundColor: 'white',
	},
	rail: {
		height: 5,
		backgroundColor: 'white',
		borderRadius: 4,
	},
})(Slider);

const StyledMenu = withStyles({
	paper: {
		backgroundColor: 'rgba(0, 0, 0, 0.9)',
		color: 'white',
		padding: '0px',
	},
})(Menu);

const useStyles = makeStyles({
	menuItem: {
		'&:hover': {
			backgroundColor: 'rgba(255, 255, 255, 0.2)', // White with 20% opacity on hover
		},
	},
});

const ValueLabelComponent = ({ children, open, value }) => (
	<Tooltip enterTouchDelay={0} open={open} placement="top" title={value}>
		{children}
	</Tooltip>
);

const H264Controls = ({
	room,
	playerRef,
	boxRef,
	maxDuration,
	handleSliderChange,
	handleSliderChangeCommitted,
	handleSliderChangeStart,
	onVolumeChange,
	onPlaybackSpeedChange,
	playbackSpeed,
	handleSkip,
	initialPosition,
	currentTime,
	// MJPEG-specific props
	handlePlayPause,
	isPlaying: mjpegIsPlaying,
	isMjpeg = false,
}) => {
	const playbackSpeeds = [
		{ name: '1x', value: 100 },
		{ name: '1.5x', value: 150 },
		{ name: '2x', value: 200 },
	];
	const { volume } = useHistoryVideo();
	const [anchorEl, setAnchorEl] = useState(null);

	// State variables
	const [isPlaying, setIsPlaying] = useState(false);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [currentSliderMaxValue, setCurrentSliderMaxValue] = useState(maxDuration);
	const [currentSliderValue, setCurrentSliderValue] = useState(currentTime);
	const classes = useStyles();

	// Use MJPEG playing state if available, otherwise use regular video state
	const currentPlayingState = isMjpeg ? mjpegIsPlaying : isPlaying;

	useEffect(() => {
		const newDuration = maxDuration * (playbackSpeed / 100);
		const newSliderValue = currentTime * (playbackSpeed / 100);
		setCurrentSliderMaxValue(newDuration);
		setCurrentSliderValue(newSliderValue);
	}, [playbackSpeed, currentTime]);

	useEffect(() => {
		const player = playerRef.current;
		if (!player || isMjpeg) return;

		const handlePlay = () => {
			setIsPlaying(true);
		};

		const handlePause = () => {
			setIsPlaying(false);
		};

		const handleFullscreenChange = () => {
			setIsFullscreen(!!document.fullscreenElement);
		};

		player.addEventListener('play', handlePlay);
		player.addEventListener('pause', handlePause);
		document.addEventListener('fullscreenchange', handleFullscreenChange);

		// Set initial states
		if (!isMjpeg) {
			setIsPlaying(!player.paused);
		}
		setIsFullscreen(!!document.fullscreenElement);

		return () => {
			player.removeEventListener('play', handlePlay);
			player.removeEventListener('pause', handlePause);
			document.removeEventListener('fullscreenchange', handleFullscreenChange);
		};
	}, [playerRef]);

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const toggleMute = () => {
		const player = playerRef.current;
		if (player) {
			player.muted = !player.muted;
			onVolumeChange(player.muted ? 0 : player.volume || 0.5);
		}
	};

	const togglePlay = () => {
		if (isMjpeg && handlePlayPause) {
			// Use MJPEG-specific play/pause handler
			handlePlayPause();
		} else {
			// Use regular video element play/pause
			const player = playerRef.current;
			if (player) {
				if (player.paused || player.ended) {
					player.play().catch((e) => {
						console.error('Error playing video:', e);
					});
				} else {
					player.pause();
				}
			}
		}
	};

	const toggleFullScreen = () => {
		if (document.fullscreenElement) {
			document.exitFullscreen();
		} else {
			boxRef.current?.requestFullscreen();
		}
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleSpeedSelect = (speedValue) => {
		setAnchorEl(null);
		onPlaybackSpeedChange(speedValue); // speedValue is 100, 150, or 200
	};

	return (
		<div className="video-controls z-99 rounded-b-lg">
			<div
				className="flex flex-col gap-4 px-16 py-16 transition-all w-full transform"
				style={{
					backgroundImage: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.5))',
					transition: 'all .3s ease',
				}}
			>
				<div className="video-progress flex-grow">
					<div className="relative bottom-10">
						<TimeSlider
							ValueLabelComponent={ValueLabelComponent}
							max={currentSliderMaxValue}
							min={0}
							onChange={handleSliderChange}
							onChangeCommitted={handleSliderChangeCommitted}
							onMouseDown={handleSliderChangeStart}
							size="large"
							step={0.1}
							value={currentSliderValue}
							valueLabelFormat={(value) => {
								if (initialPosition !== null && initialPosition !== undefined) {
									return dayjs(initialPosition + (value / (playbackSpeed / 100)) * 1000).format('H:mm:ss');
								} else {
									return dayjs(value * 1000).format('H:mm:ss');
								}
							}}
						/>
					</div>
				</div>

				<div className="flex justify-between">
					<div className="flex text-white gap-16">
						<button aria-label={currentPlayingState ? 'Pause' : 'Play'} onClick={togglePlay} type="button">
							{currentPlayingState ? <PauseIcon className="size-24" /> : <PlayIcon className="size-24" />}
						</button>

						<div className="flex items-center gap-16">
							<button aria-label="Mute" onClick={toggleMute} type="button">
								{+volume === 0 ? (
									<MuteIcon className="size-24" />
								) : (
									<VolumeIcon className="size-24" isLowVolume={+volume < 0.5} />
								)}
							</button>

							<input
								className="volume rounded-lg hidden sm:block"
								max="1"
								min="0"
								onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
								step="0.01"
								type="range"
								value={volume}
							/>
						</div>
					</div>
					<div className="flex items-center text-white">
						{/* Skip Back Buttons */}
						<div className="flex gap-16">
							<button
								className="skip-button"
								onClick={() => handleSkip(-30 * (playbackSpeed / 100))}
								title="Skip back 30 seconds"
								type="button"
							>
								<BackThirtyIcon className="size-24" />
							</button>
							<button
								className="skip-button text-white"
								onClick={() => handleSkip(-60 * (playbackSpeed / 100))}
								title="Skip back 60 seconds"
								type="button"
							>
								<BackSixtyIcon className="size-24" />
							</button>
						</div>

						<div>
							<Button aria-controls="simple-menu" aria-haspopup="true" onClick={handleClick} type="button">
								<SettingsIcon className="size-24 text-white" />
							</Button>
							<StyledMenu
								anchorEl={anchorEl}
								container={boxRef.current} // Ensure it's visible in fullscreen
								id="simple-menu"
								onClose={handleClose}
								open={Boolean(anchorEl)}
							>
								<div className="">
									<p className="mb-8 px-16">Playback Speed</p>
									{playbackSpeeds.map((speedOption) => {
										const isSelected = playbackSpeed === speedOption.value;
										return (
											<MenuItem
												className={classes.menuItem}
												key={speedOption.value}
												onClick={() => (!isSelected ? handleSpeedSelect(speedOption.value) : handleClose())}
												selected={isSelected}
											>
												{speedOption.name}
											</MenuItem>
										);
									})}
								</div>
							</StyledMenu>
						</div>
						<button aria-label="Toggle Fullscreen" onClick={toggleFullScreen} type="button">
							{isFullscreen ? <MinimizeIcon className="size-24" /> : <MaximizeIcon className="size-24" />}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default H264Controls;
